import { useEffect, useState } from "react";
import { Info, Sparkles, X } from "lucide-react";
import { api } from "@/app/api";
import { Card } from "@/app/ui";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700", eligible: "bg-sky-50 text-sky-700",
  provisioned: "bg-indigo-50 text-indigo-700", active: "bg-emerald-50 text-emerald-700",
  expired: "bg-slate-100 text-slate-500", void: "bg-rose-50 text-rose-700",
};

/** Learner-facing MyAE benefit card — shows the entitlement status with the
 * agreed learner wording and the "What is MyAE?" info popup. Never exposes
 * commercial/commission information. */
export function BenefitCard() {
  const [data, setData] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    api.get("/entitlements/me").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.benefit?.active) return null;
  const entitlement = data.entitlements?.[0];
  const benefit = data.benefit;

  return (
    <Card className="mt-6" data-testid="myae-benefit-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cobalt/10 text-cobalt"><Sparkles className="h-4.5 w-4.5" /></span>
          <div>
            <h2 className="font-display text-lg font-bold text-ink">
              {benefit.name} benefit
              <button onClick={() => setShowInfo(true)} data-testid="myae-info-button"
                className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-slatey hover:bg-surface hover:text-cobalt" aria-label={`What is ${benefit.name}?`}>
                <Info className="h-4 w-4" />
              </button>
            </h2>
            {entitlement ? (
              <>
                <p className="mt-1 text-sm text-slatey" data-testid="myae-message">{entitlement.message}</p>
                {entitlement.status === "active" && entitlement.expires_at && (
                  <p className="mt-1 text-xs font-semibold text-slatey" data-testid="myae-expiry">Active until {entitlement.expires_at.slice(0, 10)}</p>
                )}
              </>
            ) : (
              <p className="mt-1 text-sm text-slatey" data-testid="myae-general">{benefit.description}</p>
            )}
          </div>
        </div>
        {entitlement && (
          <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[entitlement.status] || STATUS_STYLE.pending}`} data-testid="myae-status">
            {entitlement.status}
          </span>
        )}
      </div>

      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onClick={() => setShowInfo(false)} data-testid="myae-popup">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold text-ink">What is {benefit.name}?</h3>
              <button onClick={() => setShowInfo(false)} data-testid="myae-popup-close" className="rounded-full p-1 text-slatey hover:bg-surface"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-3 text-sm leading-6 text-slatey">{benefit.popup_text}</p>
          </div>
        </div>
      )}
    </Card>
  );
}
